<template>
  <div class="flex flex-column gap-3">
    <span class="ant-col-24 ant-form-item-label">PDF</span>
    <div class="flex justify-content-between ant-col-24 pb-1" data-testid="document-form-pdf-controls">
      <a-upload
        :action="null"
        :customRequest="customRequestHandler"
        :showUploadList="false"
        :before-upload="loadPdf"
        accept=".pdf"
      >
        <a-button data-testid="document-form-pdf-upload-button">Upload PDF</a-button>
      </a-upload>
      <a-button @click="$emit('preview')" data-testid="document-form-pdf-preview-button">Preview</a-button>
    </div>
    <div class="ant-col-24">
      <a-alert
        message="Right Click to Add Placeholder"
        type="info"
        show-icon
        data-testid="document-form-pdf-placeholder-alert"
      />
    </div>
    <div
      v-show="pdf"
      ref="pdfContainer"
      :class="{
        'fullscreen-container': isFullscreen,
        'pdf-container-base': true
      }"
      data-testid="document-form-pdf-canvas"
    >
      <div class="fullscreen flex justify-content-between">
        <div class="flex gap-2">
          <a-tooltip>
            <template slot="title"> Fullscreen </template>
            <a-button
              class="pdf-action-button flex justify-content-center align-items-center"
              @click="toggleFullscreen"
              :type="isFullscreen ? 'primary' : 'default'"
            >
              <a-icon
                width="20"
                height="20"
                :icon="isFullscreen ? 'material-symbols:close-fullscreen' : 'material-symbols:fullscreen'"
              />
            </a-button>
          </a-tooltip>
          <a-tooltip>
            <template slot="title"> {{ showResize ? 'Stop' : 'Start' }} Resize </template>
            <a-button
              class="pdf-action-button flex justify-content-center align-items-center"
              :type="showResize ? 'primary' : 'default'"
              @click="showResize = !showResize"
            >
              <a-icon width="20" height="20" icon="material-symbols:resize" />
            </a-button>
          </a-tooltip>
          <a-tooltip class="pdf-ui-ignore">
            <template slot="title"> {{ showDragging ? 'Stop' : 'Start' }} Moving </template>
            <a-button
              class="pdf-action-button flex justify-content-center align-items-center"
              :type="showDragging ? 'primary' : 'default'"
              @click="showDragging = !showDragging"
            >
              <a-icon
                width="20"
                height="20"
                :icon="showDragging ? 'material-symbols:lock-outline' : 'material-symbols:lock-open-right-outline'"
              />
            </a-button>
          </a-tooltip>
          <a-tooltip class="pdf-ui-ignore">
            <template slot="title"> {{ showSelect ? 'Stop' : 'Start' }} Select </template>
            <a-button
              class="pdf-action-button flex justify-content-center align-items-center"
              :type="showSelect ? 'primary' : 'default'"
              @click="handleSelectClick"
            >
              <a-icon
                width="20"
                height="20"
                :icon="showSelect ? 'material-symbols:remove-selection' : 'material-symbols:select'"
              />
            </a-button>
          </a-tooltip>
          <!-- ✅ ZOOM IN -->
          <a-tooltip>
            <template slot="title"> Zoom In (Ctrl/Cmd +) </template>
            <a-button class="pdf-action-button flex justify-content-center align-items-center" @click="zoomIn" type="default">
              <a-icon width="20" height="20" icon="material-symbols:add" />
            </a-button>
          </a-tooltip>

          <!-- ✅ ZOOM OUT -->
          <a-tooltip>
            <template slot="title"> Zoom Out (Ctrl/Cmd -) </template>
            <a-button class="pdf-action-button flex justify-content-center align-items-center" @click="zoomOut" type="default">
              <a-icon width="20" height="20" icon="material-symbols:remove" />
            </a-button>
          </a-tooltip>

          <!-- ✅ RESET ZOOM -->
          <a-tooltip>
            <template slot="title"> Reset Zoom (Ctrl/Cmd 0) </template>
            <a-button class="pdf-action-button flex justify-content-center align-items-center" @click="resetZoom" type="default">
              <a-icon width="20" height="20" icon="material-symbols:refresh" />
            </a-button>
          </a-tooltip>
        </div>
        <div class="flex gap-2">
          <a-button
            class="pdf-action-button flex justify-content-center align-items-center"
            @click="undo"
            :disabled="historyIndex <= 0"
            type="default"
          >
            <a-icon width="20" height="20" icon="material-symbols:undo" />
          </a-button>
          <a-button
            class="pdf-action-button flex justify-content-center align-items-center"
            @click="redo"
            :disabled="historyIndex >= history.length - 1"
            type="default"
          >
            <a-icon width="20" height="20" icon="material-symbols:redo" />
          </a-button>
        </div>
      </div>
      <div v-for="(page, index) in pages" :key="index" class="pdf-page">
        <canvas
          :ref="'page_' + (index + 1)"
          @contextmenu.prevent="showContextMenu($event, index)"
          @click.prevent="closeMenu"
        ></canvas>
        <div
          v-for="(placeholder, pIndex) in placeholders.filter(p => p.page === index)"
          :key="pIndex"
          class="placeholder"
          :ref="`placeholder-${pIndex}`"
          :data-id="`${placeholder.id}`"
          :style="{
            left: getScaledPosition(placeholder).x + 'px',
            top: getScaledPosition(placeholder).y + 'px',
            width: getScaledPosition(placeholder).width + 'px',
            height: getScaledPosition(placeholder).height + 'px',
            border: isSelected(placeholder.id)
              ? '1px solid #3b82f6'
              : hasOverflowY(pIndex)
                ? '1px solid red'
                : '1px solid black',
            cursor: showDragging ? 'grab' : 'default'
          }"
          @mousedown="showDragging ? startDrag($event, placeholder) : () => {}"
        >
          <template v-if="showResize">
            <div class="resize-handle top" @mousedown.stop="startResize($event, placeholder, 'top')"></div>
            <div class="resize-handle bottom" @mousedown.stop="startResize($event, placeholder, 'bottom')"></div>
            <div class="resize-handle left" @mousedown.stop="startResize($event, placeholder, 'left')"></div>
            <div class="resize-handle right" @mousedown.stop="startResize($event, placeholder, 'right')"></div>
            <div
              class="resize-handle corner-top-left"
              @mousedown.stop="startResize($event, placeholder, 'corner-top-left')"
            ></div>
            <div
              class="resize-handle corner-top-right"
              @mousedown.stop="startResize($event, placeholder, 'corner-top-right')"
            ></div>
            <div
              class="resize-handle corner-bottom-left"
              @mousedown.stop="startResize($event, placeholder, 'corner-bottom-left')"
            ></div>
            <div
              class="resize-handle corner-bottom-right"
              @mousedown.stop="startResize($event, placeholder, 'corner-bottom-right')"
            ></div>
          </template>
          <div
            :style="{
              width: '100%',
              height: '100%',
              overflow: 'hidden'
            }"
          >
            <a-tooltip :getPopupContainer="getPopupContainer">
              <template slot="title">
                {{ placeholder.text }}
              </template>
              <a-dropdown
                :trigger="['contextmenu']"
                :getPopupContainer="getPopupContainer"
                :style="{
                  display: 'flex',
                  width: '100%',
                  height: '100%',
                  overflow: 'auto'
                }"
              >
                <div
                  :style="{
                    width: '100%',
                    height: '100%',
                    minHeight: 'fit-content'
                  }"
                >
                  <a-mentions
                    placeholder="#Placeholder"
                    :prefix="['#']"
                    :getPopupContainer="getPopupContainer"
                    :style="{
                      width: '100%',
                      height: '100%',
                      overflow: 'hidden',
                      fontSize: `clamp(4px, ${Math.min(
                        getScaledPosition(placeholder).height,
                        getScaledPosition(placeholder).width
                      )}px, 24px)`,
                      margin: 0
                    }"
                    :class="
                      'placeholder-input' +
                      (placeholder.required ? ' required-input' : '') +
                      (showDragging ? ' grab' : '') +
                      (draggingPlaceholder ? ' grabbing' : '')
                    "
                    :value="placeholder.text ?? ''"
                    @change="value => handleChange(value, placeholder.id)"
                  >
                    <a-mentions-option
                      v-for="placeholder in availablePlaceholders"
                      :key="placeholder"
                      :value="placeholder"
                    >
                      {{ placeholder }}
                    </a-mentions-option>
                  </a-mentions>
                </div>
                <a-menu slot="overlay">
                  <a-menu-item
                    key="copy"
                    @click="
                      () => {
                        selectedPlaceholders = [placeholder.id]
                        copySelected()
                      }
                    "
                  >
                    Copy
                  </a-menu-item>
                  <a-menu-item key="required" @click="placeholder.required = !placeholder.required">
                    {{ placeholder.required ? 'Make Optional' : 'Make Required' }}
                  </a-menu-item>
                  <a-menu-item key="clear" @click="placeholder.text = ''"> Clear </a-menu-item>
                  <a-menu-item key="delete" @click="deleteField(placeholder.id)"> Delete </a-menu-item>
                </a-menu>
              </a-dropdown>
            </a-tooltip>
          </div>
        </div>
        <a-dropdown
          v-if="showMenu && menuPage === index"
          :visible="showMenu"
          :overlay="contextMenu"
          :getPopupContainer="getPopupContainer"
          :style="{
            left: menuPosition.x + 'px',
            top: menuPosition.y + 'px',
            position: 'absolute'
          }"
          @click="closeMenu"
        >
          <div></div>
        </a-dropdown>
      </div>
      <div
        v-if="lastSelectionRect && lastSelectionRect.width > 0 && lastSelectionRect.height > 0"
        :style="{
          position: 'absolute',
          left: lastSelectionRect.x + 'px',
          top: lastSelectionRect.y + 'px',
          width: lastSelectionRect.width + 'px',
          height: lastSelectionRect.height + 'px',
          border: '1px dashed #3b82f6',
          backgroundColor: 'rgba(24, 144, 255, 0.05)',
          pointerEvents: 'none',
          zIndex: 98
        }"
      ></div>
      <div
        v-if="isSelecting && selectionRect.width > 0 && selectionRect.height > 0"
        :style="{
          position: 'absolute',
          left: selectionRect.x + 'px',
          top: selectionRect.y + 'px',
          width: selectionRect.width + 'px',
          height: selectionRect.height + 'px',
          border: '1px dashed #3b82f6',
          backgroundColor: 'rgba(24, 144, 255, 0.1)',
          pointerEvents: 'none',
          zIndex: 99
        }"
      ></div>
    </div>
  </div>
</template>
<script>
import {
  Alert,
  Button,
  Dropdown,
  Input,
  Mentions,
  Menu,
  Modal,
  notification,
  Select,
  Tooltip,
  Upload
} from 'ant-design-vue'
import { PDFArray, PDFCheckBox, PDFDocument, PDFTextField } from 'pdf-lib'
import { v4 as uuidv4 } from 'uuid'
import { createNamespacedHelpers } from 'vuex'
import PDFHistoryMixin from '../mixins/PDFHistoryMixin'
import { arraysDeepEqual, deepCopy } from '@/utils/deep-copy'
import PDFSelectMixin from '../mixins/PDFSelectMixin'
const pdfjsLib = require('pdfjs-dist')
pdfjsLib.GlobalWorkerOptions.workerSrc = require('pdfjs-dist/build/pdf.worker.entry.js')
const { mapState: documentState } = createNamespacedHelpers('document')

export default {
  components: {
    'a-upload': Upload,
    'a-button': Button,
    'a-input': Input,
    'a-dropdown': Dropdown,
    'a-menu': Menu,
    'a-menu-item': Menu.Item,
    'a-sub-menu': Menu.SubMenu,
    'a-select': Select,
    'a-select-option': Select.Option,
    'a-tooltip': Tooltip,
    'a-mentions': Mentions,
    'a-mentions-option': Mentions.Option,
    'a-alert': Alert
  },
  mixins: [PDFHistoryMixin, PDFSelectMixin],
  props: {
    file: {
      type: String,
      default: undefined
    },
    isPlaceholdersChanges: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      pdf: null,
      pages: [],
      initialPlaceholders: [],
      placeholders: [],
      pageScales: [],
      pdfBytes: null,
      draggingIndex: null,
      showMenu: false,
      menuPosition: { x: 0, y: 0 },
      menuPage: null,
      showResize: false,
      showDragging: false,
      showSelect: false,
      isFullscreen: false,
      draggingPlaceholder: null,
      currentPlaceholder: null,
      draggingOffset: null,
      resizeObserver: null,
      resizeTimeout: null, // Debounce
      renderSessionId: 0,
      renderQueue: Promise.resolve(),
      activeRenderTasks: {},

      zoomLevel: 1.0,
      zoomStep: 0.1,
      minZoom: 0.5,
      maxZoom: 3.0
    }
  },
  emits: ['save'],
  computed: {
    ...documentState({
      availablePlaceholders: state => state.placeholders
    }),
    contextMenu() {
      return (
        <a-menu class="pdf-ui-ignore">
          <a-sub-menu key="field" title="Add Field">
            <a-menu-item onClick={() => this.addPlaceholder('PDFTextField')}>Text</a-menu-item>
            <a-menu-item onClick={() => this.addPlaceholder('PDFCheckBox')}>Checkbox</a-menu-item>
          </a-sub-menu>
          {this.selectedPlaceholders.length > 0 && (
            <a-menu-item key="copy-group" onClick={() => this.copySelected()}>
              Copy Selected ({this.selectedPlaceholders.length})
            </a-menu-item>
          )}
          {this.copyElements.length > 0 && (
            <a-menu-item key="paste-group" onClick={() => this.pasteElements(this.menuPosition)}>
              Paste ({this.copyElements.length})
            </a-menu-item>
          )}
          <a-menu-item
            key="delete"
            onClick={() => {
              this.closeMenu()
              Modal.confirm({
                title: 'Are you sure?',
                content: 'You are about to delete an all placeholders. If you are sure - click OK.',
                onOk: async () => {
                  this.placeholders = []
                  this.saveToHistory()
                },
                onCancel() {}
              })
            }}
          >
            Delete All Fields
          </a-menu-item>
        </a-menu>
      )
    }
  },
  mounted() {
    const pdfContainer = this.$refs.pdfContainer
    if (pdfContainer) {
      pdfContainer.addEventListener('scroll', this.closeMenu, {
        passive: true
      })
    }
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(this.handleContainerResize)
      if (this.$refs.pdfContainer) {
        this.resizeObserver.observe(this.$refs.pdfContainer)
      }
    } else {
      window.addEventListener('resize', this.handleContainerResize)
    }
  },
  beforeDestroy() {
    const pdfContainer = this.$refs.pdfContainer
    if (pdfContainer) {
      pdfContainer.removeEventListener('scroll', this.closeMenu)
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    } else {
      window.removeEventListener('resize', this.handleContainerResize)
    }
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout)
    }
    this.cancelAllRenderTasks()
    if (this.pdf?.destroy) this.pdf.destroy()
  },
  watch: {
    placeholders: {
      handler(newVal) {
        if (this.initialPlaceholders.length && !arraysDeepEqual(this.initialPlaceholders, newVal)) {
          this.$emit('update:isPlaceholdersChanges', true)
        } else {
          this.$emit('update:isPlaceholdersChanges', false)
        }
        this.$nextTick(() => {
          this.placeholders.forEach((_, index) => {
            this.hasOverflowY(index)
          })
        })
      },
      deep: true
    },
    file: {
      async handler(newVal) {
        if (newVal) {
          const response = await fetch(newVal)
          const blob = await response.blob()
          const file = new File([blob], 'document.pdf', {
            type: 'application/pdf'
          })
          await this.loadPdf(file)
        }
      },
      immediate: true
    }
  },
  methods: {
    calculatePageScale(page) {
      const container = this.$refs.pdfContainer
      const containerWidth = container?.clientWidth || page.getViewport({ scale: 1 }).width
      const pageWidth = page.getViewport({ scale: 1 }).width
      const baseScale = containerWidth / pageWidth
      return baseScale * this.zoomLevel
    },
    roundCoords(obj) {
      const rounded = { ...obj }
      ;['x', 'y', 'width', 'height', 'size'].forEach(key => {
        if (rounded[key] !== undefined) {
          rounded[key] = Math.round(rounded[key] * 100) / 100
        }
      })
      return rounded
    },
    zoomIn() {
      if (this.zoomLevel < this.maxZoom) {
        this.zoomLevel = Math.min(this.zoomLevel + this.zoomStep, this.maxZoom)
        this.requestRenderAllPages()
      }
    },
    // ✅ ZOOM OUT
    zoomOut() {
      if (this.zoomLevel > this.minZoom) {
        this.zoomLevel = Math.max(this.zoomLevel - this.zoomStep, this.minZoom)
        this.requestRenderAllPages()
      }
    },
    // ✅ RESET ZOOM
    resetZoom() {
      this.zoomLevel = 1.0
      this.requestRenderAllPages()
    },
    requestRenderAllPages() {
      this.renderQueue = this.renderQueue
        .then(() => this.renderAllPages())
        .catch(error => {
          console.error('PDF render queue error', error)
        })
      return this.renderQueue
    },
    cancelAllRenderTasks() {
      Object.values(this.activeRenderTasks).forEach(task => {
        if (task?.cancel) task.cancel()
      })
      this.activeRenderTasks = {}
    },
    async loadPdfDocument() {
      if (!this.pdfBytes) return null
      if (this.pdf?.destroy) this.pdf.destroy()
      this.pdf = await pdfjsLib.getDocument(this.pdfBytes).promise
      return this.pdf
    },
    // Debounced resize handler to prevent performance issues
    handleContainerResize() {
      if (this.resizeTimeout) clearTimeout(this.resizeTimeout)
      this.resizeTimeout = setTimeout(() => {
        if (this.pdf) {
          this.requestRenderAllPages()
        }
      }, 150)
    },
    getPopupContainer() {
      return this.$refs.pdfContainer || document.body
    },
    getScaledPosition(placeholder) {
      const scale = this.pageScales[placeholder.page] || 1
      return {
        x: placeholder.x * scale,
        y: placeholder.y * scale,
        width: placeholder.width * scale,
        height: placeholder.height * scale,
        size: placeholder.size
      }
    },
    hasOverflowY(index) {
      const placeholderElement = this.$refs[`placeholder-${index}`]?.[0]
      if (placeholderElement) {
        return placeholderElement.scrollHeight - 5 > placeholderElement.clientHeight
      }
      return false
    },
    customRequestHandler(options) {
      options.onSuccess('success', options.file)
    },
    handleChange(value, id) {
      const placeholder = this.placeholders.find(p => p.id === id)
      if (placeholder) {
        const oldText = placeholder.text
        const newText = value.replaceAll('##', '#')
        if (oldText !== newText) {
          placeholder.text = newText
          this.saveToHistory()
        }
      }
    },
    changeResize() {
      this.showResize = !this.showResize
    },
    startResize(event, placeholder, direction) {
      this.isResizing = true
      this.currentPlaceholder = placeholder
      this.resizeDirection = direction
      this.startX = event.clientX
      this.startY = event.clientY
      this.initialWidth = placeholder.width
      this.initialHeight = placeholder.height
      this.initialX = placeholder.x
      this.initialY = placeholder.y
      // Используем специфичное имя для движения мыши при ресайзе, чтобы не конфликтовать
      document.addEventListener('mousemove', this.onPlaceholderResizeMouseMove)
      document.addEventListener('mouseup', this.stopResize)
    },
    onPlaceholderResizeMouseMove(event) {
      if (this.isResizing && this.currentPlaceholder) {
        const scale = this.pageScales[this.currentPlaceholder.page] || 1
        const deltaX = (event.clientX - this.startX) / scale
        const deltaY = (event.clientY - this.startY) / scale
        switch (this.resizeDirection) {
          case 'top':
            this.currentPlaceholder.height = this.initialHeight - deltaY
            this.currentPlaceholder.y = this.initialY + deltaY
            break
          case 'bottom':
            this.currentPlaceholder.height = this.initialHeight + deltaY
            break
          case 'left':
            this.currentPlaceholder.width = this.initialWidth - deltaX
            this.currentPlaceholder.x = this.initialX + deltaX
            break
          case 'right':
            this.currentPlaceholder.width = this.initialWidth + deltaX
            break
          case 'corner-top-left':
            this.currentPlaceholder.width = this.initialWidth - deltaX
            this.currentPlaceholder.height = this.initialHeight - deltaY
            this.currentPlaceholder.x = this.initialX + deltaX
            this.currentPlaceholder.y = this.initialY + deltaY
            break
          case 'corner-top-right':
            this.currentPlaceholder.width = this.initialWidth + deltaX
            this.currentPlaceholder.height = this.initialHeight - deltaY
            this.currentPlaceholder.y = this.initialY + deltaY
            break
          case 'corner-bottom-left':
            this.currentPlaceholder.width = this.initialWidth - deltaX
            this.currentPlaceholder.height = this.initialHeight + deltaY
            this.currentPlaceholder.x = this.initialX + deltaX
            break
          case 'corner-bottom-right':
            this.currentPlaceholder.width = this.initialWidth + deltaX
            this.currentPlaceholder.height = this.initialHeight + deltaY
            break
        }
      }
    },
    stopResize() {
      if (this.isResizing && this.currentPlaceholder) {
        this.saveToHistory()
      }
      this.isResizing = false
      this.currentPlaceholder = null
      this.resizeDirection = null
      document.removeEventListener('mousemove', this.onPlaceholderResizeMouseMove)
      document.removeEventListener('mouseup', this.stopResize)
    },
    showContextMenu(event, index) {
      const rect = event.target.getBoundingClientRect()
      const x = event.clientX - rect.left
      const y = event.clientY - rect.top
      this.menuPosition = { x, y }
      if (this.menuPage !== index) {
        this.showMenu = true
        this.menuPage = index
      } else {
        this.showMenu = false
        this.menuPage = null
      }
    },
    closeMenu() {
      this.showMenu = false
      this.menuPage = null
    },
    deleteField(id) {
      const index = this.placeholders.findIndex(p => p.id === id)
      if (index !== -1) {
        this.placeholders.splice(index, 1)
        this.saveToHistory()
      }
    },
    getPageNumberMapForFields(pdfDoc) {
      const d = {}
      const form = pdfDoc.getForm()
      const pages = pdfDoc.getPages()
      form.getFields().forEach(field => {
        form
          .getField(field.getName())
          .acroField.getWidgets()
          .forEach(w => {
            const widgetRef = pdfDoc.context.getObjectRef(w.dict)
            for (let idx = 0, len = pages.length; idx < len; idx++) {
              if (pages[idx].node.Annots()?.indexOf(widgetRef) !== undefined) {
                d[field.getName()] = idx + 1
                break
              }
            }
          })
      })
      return d
    },
    async loadPdf(file) {
      try {
        const arrayBuffer = await file.arrayBuffer()
        const pdfData = new Uint8Array(arrayBuffer)
        const pdfDoc = await PDFDocument.load(pdfData)
        const form = pdfDoc.getForm()
        const fields = form.getFields()
        const relatedPages = this.getPageNumberMapForFields(pdfDoc)
        const pages = pdfDoc.getPages()

        const widgetsRefsToRemove = new Set()

        this.placeholders = fields
          .map(field => {
            const widgets = field.acroField.getWidgets()
            if (!widgets || widgets.length === 0) return null

            const widget = widgets[0]
            widgetsRefsToRemove.add(widget.ref)

            // ✅ Rect() возвращает PDF координаты (без scale!)
            const rect = widget.Rect().asRectangle()
            const x = rect.x
            const y = rect.y
            const width = rect.width
            const height = rect.height

            const pageNumber = relatedPages[field.getName()] - 1
            const pageLib = pages[pageNumber]

            // ✅ Конвертация Y: PDF (bottom-left) → Web (top-left)
            const originalY = pageLib.getHeight() - y - height

            let text = ''
            let type = 'PDFTextField'

            if (field instanceof PDFTextField) {
              text = field.getText()
            } else if (field instanceof PDFCheckBox) {
              type = 'PDFCheckBox'
            }

            return {
              id: field.getName(),
              text: text,
              x: Math.round(x * 100) / 100,
              y: Math.round(originalY * 100) / 100,
              size: 12,
              page: pageNumber,
              height: Math.round(height * 100) / 100,
              width: Math.round(width * 100) / 100,
              type: type,
              required: field.isRequired()
            }
          })
          .filter(Boolean)

        this.initialPlaceholders = deepCopy(this.placeholders)

        // Удаляем аннотации со страниц
        pages.forEach(page => {
          try {
            const annotations = page.node.Annots()
            if (annotations) {
              const oldAnnots = annotations.asArray()
              const newAnnotsRefs = oldAnnots.filter(annot => !widgetsRefsToRemove.has(annot.ref))

              const newAnnotsArray = PDFArray.withContext(pdfDoc.context)
              newAnnotsRefs.forEach(ref => newAnnotsArray.push(ref))

              const annotsKey = pdfDoc.context.obj('Annots')
              page.node.dict.set(annotsKey, newAnnotsArray)
            }
          } catch (err) {
            console.warn(`Не удалось обновить аннотации на странице ${page.index}:`, err)
          }
        })

        // Очищаем AcroForm
        try {
          const acroForm = pdfDoc.catalog.getOrCreateAcroForm()
          acroForm.dict.delete(pdfDoc.context.obj('Fields'))
        } catch (e) {
          console.warn('Не удалось очистить AcroForm:', e)
        }

        this.pdfBytes = await pdfDoc.save()

        await this.$nextTick()
        await this.requestRenderAllPages()
        this.saveToHistory()
      } catch (error) {
        console.error('Ошибка загрузки PDF:', error)
      }
      return false
    },
    async addPlaceholder(type) {
      const uuid = `placeholder_${uuidv4()}`
      const scale = this.pageScales[this.menuPage] || 1
      const x = this.menuPosition.x / scale
      const y = this.menuPosition.y / scale

      const newPlaceholder = {
        page: this.menuPage,
        id: uuid,
        x,
        y,
        size: 12,
        type: type,
        text: '',
        height: 20,
        width: type === 'PDFCheckBox' ? 20 : 200,
        required: false
      }

      // ✅ Округляем новый placeholder
      this.placeholders.push(this.roundCoords(newPlaceholder))
      await this.renderPageWithPlaceholders(this.menuPage, uuid)
      this.closeMenu()
      this.saveToHistory()
    },
    async renderPageWithPlaceholders(pageNumber, uuid) {
      const page = await this.pdf.getPage(pageNumber + 1)
      const canvas = this.$refs[`page_${pageNumber + 1}`][0]
      const pageKey = pageNumber + 1

      const scale = this.calculatePageScale(page)
      const viewport = page.getViewport({ scale })
      const outputScale = 1

      canvas.width = Math.round(viewport.width * outputScale)
      canvas.height = Math.round(viewport.height * outputScale)
      canvas.style.width = `${viewport.width}px`
      canvas.style.height = `${viewport.height}px`

      const context = canvas.getContext('2d')
      context.scale(outputScale, outputScale)

      const existingTask = this.activeRenderTasks[pageKey]
      if (existingTask?.cancel) existingTask.cancel()

      const renderTask = page.render({
        canvasContext: context,
        viewport: viewport,
        renderInteractiveForms: false
      })
      this.activeRenderTasks[pageKey] = renderTask

      try {
        await renderTask.promise
      } catch (error) {
        if (error?.name !== 'RenderingCancelledException') throw error
      } finally {
        if (this.activeRenderTasks[pageKey] === renderTask) {
          delete this.activeRenderTasks[pageKey]
        }
      }

      this.$set(this.pageScales, pageNumber, scale)
    },
    async renderAllPages() {
      if (!this.pdfBytes) return

      const currentSessionId = ++this.renderSessionId
      this.cancelAllRenderTasks()
      this.pdf = await this.loadPdfDocument()
      if (!this.pdf || currentSessionId !== this.renderSessionId) return

      this.pages = Array.from({ length: this.pdf.numPages }, (_, i) => i + 1)
      await this.$nextTick()

      let cumulativeHeight = 0
      this.pageOffsets = []

      for (const pageNumber of this.pages) {
        if (currentSessionId !== this.renderSessionId) return
        const page = await this.pdf.getPage(pageNumber)
        const canvasArray = this.$refs[`page_${pageNumber}`]
        const canvas = Array.isArray(canvasArray) ? canvasArray[0] : canvasArray
        if (!canvas) continue

        // ✅ Расчёт scale
        const container = this.$refs.pdfContainer
        const containerWidth = container?.clientWidth || page.getViewport({ scale: 1 }).width
        const pageWidth = page.getViewport({ scale: 1 }).width
        const baseScale = containerWidth / pageWidth
        const scale = baseScale * this.zoomLevel

        const viewport = page.getViewport({ scale })
        const outputScale = 1

        // ✅ Canvas размеры
        canvas.width = Math.round(viewport.width * outputScale)
        canvas.height = Math.round(viewport.height * outputScale)
        canvas.style.width = `${Math.round(viewport.width)}px`
        canvas.style.height = `${Math.round(viewport.height)}px`

        const context = canvas.getContext('2d')
        context.scale(outputScale, outputScale)

        const existingTask = this.activeRenderTasks[pageNumber]
        if (existingTask?.cancel) existingTask.cancel()

        const renderTask = page.render({
          canvasContext: context,
          viewport: viewport,
          renderInteractiveForms: false
        })
        this.activeRenderTasks[pageNumber] = renderTask

        try {
          await renderTask.promise
        } catch (error) {
          if (error?.name !== 'RenderingCancelledException') throw error
        } finally {
          if (this.activeRenderTasks[pageNumber] === renderTask) {
            delete this.activeRenderTasks[pageNumber]
          }
        }

        this.$set(this.pageScales, pageNumber - 1, scale)
        this.$set(this.pageOffsets, pageNumber - 1, cumulativeHeight)
        cumulativeHeight += viewport.height
      }
    },
    async savePdf() {
      if (this.pdfBytes === null) {
        throw new Error('Please select a document')
      }

      const pdfDoc = await PDFDocument.load(this.pdfBytes)
      const form = pdfDoc.getForm()

      // Удаляем все существующие поля
      const existingFields = form.getFields()
      existingFields.forEach(field => {
        try {
          form.removeField(field)
        } catch (e) {
          console.warn('Не удалось удалить поле:', e)
        }
      })

      // Очищаем AcroForm вручную (чтобы избежать ошибок appearance)
      try {
        const acroForm = pdfDoc.catalog.getOrCreateAcroForm()
        acroForm.dict.delete(pdfDoc.context.obj('Fields'))
      } catch (e) {
        console.warn('Не удалось очистить AcroForm:', e)
      }

      this.placeholders.forEach(item => {
        const pageLib = pdfDoc.getPages()[item.page]

        // ✅ ВАЖНО: pageLib.getHeight() может отличаться от того, что было при загрузке!
        const pageHeight = pageLib.getHeight()

        let field
        if (item.type === 'PDFTextField') {
          field = form.createTextField(item.id)
          field.setText(item?.text ? item.text.replaceAll('  ', ' ').trim() : '')
        } else if (item.type === 'PDFCheckBox') {
          field = form.createCheckBox(item.id)
        }

        if (field) {
          item.required ? field.enableRequired() : field.disableRequired()

          // ✅ Конвертация координат: Web (top-left) → PDF (bottom-left)
          field.addToPage(pageLib, {
            x: item.x,
            y: pageHeight - item.y - item.height, // PDF Y идёт снизу вверх
            height: item.height,
            width: item.width,
            borderWidth: 0
          })
        }
      })

      this.pdfBytes = await pdfDoc.save()

      const blob = new Blob([this.pdfBytes], { type: 'application/pdf' })
      const file = new File([blob], 'document.pdf', {
        type: 'application/pdf'
      })
      await this.uploadingFile(file)
    },
    async uploadingFile(file) {
      try {
        const data = await this.$store.dispatch('uploadFiles', {
          files: [file]
        })
        if (data.error_code && data.error_code !== '0') {
          notification.warning({
            message: 'Upload Error',
            description: data.mag ?? 'Please try again to upload document'
          })
          return
        }
        if (data.ids.length) {
          this.$emit('save', data.ids[0])
        }
      } catch (err) {
        throw new Error(err)
      }
    },
    toggleFullscreen() {
      if (!this.isFullscreen) {
        this.enterFullscreen()
      } else {
        this.exitFullscreen()
      }
    },
    enterFullscreen() {
      const elem = this.$refs.pdfContainer
      if (!elem) return
      if (elem.requestFullscreen) {
        elem.requestFullscreen()
      } else if (elem.mozRequestFullScreen) {
        elem.mozRequestFullScreen()
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen()
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen()
      } else {
        console.warn('Fullscreen API not supported')
        return
      }
      this.isFullscreen = true
      document.addEventListener('fullscreenchange', this.handleFullscreenChange)
      document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange)
      document.addEventListener('mozfullscreenchange', this.handleFullscreenChange)
      document.addEventListener('MSFullscreenChange', this.handleFullscreenChange)
    },
    exitFullscreen() {
      if (document.exitFullscreen) {
        document.exitFullscreen()
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen()
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen()
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen()
      }
    },
    handleFullscreenChange() {
      this.isFullscreen =
        !!document.fullscreenElement ||
        !!document.webkitFullscreenElement ||
        !!document.mozFullScreenElement ||
        !!document.msFullscreenElement
      this.$nextTick(() => {
        if (this.pdf) {
          this.requestRenderAllPages()
        }
      })
    }
  }
}
</script>
<style scoped>
canvas {
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
  image-rendering: pixelated;
}

.pdf-container-base {
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  border: 1px solid var(--border-color-base);
  height: 600px;
  box-sizing: border-box; /* FIX: Важно для стабильности размеров */
  position: relative;
}

.pdf-page {
  position: relative;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.placeholder {
  position: absolute;
  display: inline-flex;
  border: 1px solid #000;
  background-color: rgb(255, 255, 255);
  transform: translate(0, 0);
  will-change: auto;
}
.placeholder-input {
  border: none;
  background: transparent;
  outline: none;
  font-size: 12px;
  padding: 0;
  width: 100%;
}
::v-deep .required-input.ant-mentions > textarea {
  background-color: rgba(253, 156, 156, 0.4);
  border-radius: 0;
}
::v-deep .grab.ant-mentions > textarea {
  cursor: grab;
}
::v-deep .grabbing.ant-mentions > textarea {
  cursor: grabbing;
}
::v-deep .ant-select-auto-complete.ant-select .ant-input {
  height: 100%;
  padding: 0 12px;
}
::v-deep .ant-btn-icon-only {
  height: 100%;
}
::v-deep .ant-select .ant-select-selection__rendered {
  line-height: 20px;
}
::v-deep .ant-select-selection:focus {
  border: none;
  box-shadow: none;
}
::v-deep .ant-select-selection:active {
  border: none;
  box-shadow: none;
}
::v-deep .ant-select-selection {
  border: none;
  box-shadow: none;
  height: 100%;
  padding: 0;
}
::v-deep .ant-select-selection--multiple {
  min-height: auto;
}
.placeholder:active {
  cursor: grabbing;
}
.resize-handle {
  position: absolute;
  width: 5px;
  height: 5px;
  background-color: #007bff;
  cursor: pointer;
  z-index: 100;
}
.resize-handle.top {
  top: 0;
  left: 50%;
  width: 100%;
  height: 2px;
  transform: translateX(-50%);
  cursor: n-resize;
}
.resize-handle.bottom {
  bottom: 0;
  left: 50%;
  width: 100%;
  height: 2px;
  transform: translateX(-50%);
  cursor: s-resize;
}
.resize-handle.left {
  left: 0;
  top: 50%;
  width: 2px;
  height: 100%;
  transform: translateY(-50%);
  cursor: w-resize;
}
.resize-handle.right {
  right: 0;
  top: 50%;
  width: 2px;
  height: 100%;
  transform: translateY(-50%);
  cursor: e-resize;
}
.resize-handle.corner-top-left {
  top: -5px;
  left: -5px;
  cursor: nw-resize;
}
.resize-handle.corner-top-right {
  top: -5px;
  right: -5px;
  cursor: ne-resize;
}
.resize-handle.corner-bottom-left {
  bottom: -5px;
  left: -5px;
  cursor: sw-resize;
}
.resize-handle.corner-bottom-right {
  bottom: -5px;
  right: -5px;
  cursor: se-resize;
}
.fullscreen {
  width: 100%;
  min-height: 44px;
  padding: 6px 8px;
  position: sticky;
  top: 0;
  left: 0;
  z-index: 100;
  box-sizing: border-box;
  background-color: var(--bg-component);
  border-bottom: 1px solid var(--border-color-base);
  box-shadow: var(--shadow-base);
}

.pdf-action-button.ant-btn-default {
  color: var(--text-color);
  background-color: transparent;
  border-color: transparent;
  box-shadow: none;
}

.pdf-action-button.ant-btn-default:not([disabled]):hover {
  color: var(--text-color);
  background-color: var(--bg-row-hover);
  border-color: var(--border-color-base);
}

.pdf-action-button.ant-btn-default:not([disabled]):focus {
  color: var(--text-color);
  background-color: var(--bg-row-hover);
  border-color: var(--primary-color);
}

.pdf-action-button.ant-btn-default[disabled] {
  color: var(--text-disabled);
  background-color: transparent;
  border-color: transparent;
  opacity: 0.6;
}
.fullscreen-container {
  height: 100vh !important;
  width: 100vw !important;
  overflow: auto;
  position: relative;
}
::v-deep .ant-mentions > textarea {
  min-height: 100%;
  height: 100%;
  max-height: 100%;
  padding: 0;
  line-height: 1;
}
</style>
